/**
 * GAB tracking - dataLayer pushes for the L'Oreal / Jellyfish tracking plan.
 * Spec: docs/tracking-plan/README.md
 *
 * Loaded synchronously from snippets/gab-tracking.liquid. Once OneTrust is
 * ready it pushes pageDataReady, then loads the GTM container (G.loadGtm).
 *
 * Pushes from the theme: pageDataReady, view_item, add_to_cart, begin_checkout.
 * add_payment_info and purchase happen inside Shopify checkout, which the theme
 * cannot reach - see checkout-pixel/gab-checkout-pixel.js.
 */
(function () {
    'use strict';

    var G = window.GABTracking;
    if (!G || G.loaded) return;
    G.loaded = true;

    var CONFIG = {
        brand: 'GIO',
        siteSection: 'standard site',
        websiteType: 'D2C',
        // Products carrying one of these tags are gifts with purchase, not sales.
        giftTags: ['bogos-gift'],
        checkoutSelector: '#cart-checkout, #cart-sidebar-checkout, .button-checkout:not(.button-disable), [name="checkout"], a[href$="/checkout"]',
        // Shared with the checkout pixel so it can send the same product dimensions.
        storageKey: 'gab_tracking_products',
        // Shopify product type -> axis (lowercase).
        axes: {
            'makeup': 'makeup', 'foundation': 'makeup', 'concealer': 'makeup', 'powder': 'makeup', 'primer': 'makeup',
            'highlighter': 'makeup', 'blush and bronzer': 'makeup', 'lipstick': 'makeup', 'liquid lipstick': 'makeup',
            'lip gloss': 'makeup', 'lip balm': 'makeup', 'mascara': 'makeup', 'eyeliner': 'makeup', 'eyeshadow': 'makeup',
            'fragrance': 'fragrance', "women's fragrance": 'fragrance', "men's fragrance": 'fragrance', 'emporio armani': 'fragrance',
            'les eaux': 'fragrance', 'les terres précieuses': 'fragrance', 'les mille et une nuits': 'fragrance',
            'candles collection': 'fragrance', 'bath & body collection': 'fragrance',
            'anti-aging': 'skincare', 'hydrating': 'skincare', 'lifting and tightening': 'skincare',
            'uv protection, pollution, cleansing': 'skincare'
        },
        // Product tag -> axis, used when the product type is not in the list above.
        tagAxes: {
            'face': 'makeup', 'lips': 'makeup', 'eyes': 'makeup', 'makeup': 'makeup',
            'men': 'fragrance', 'women': 'fragrance', 'unisex': 'fragrance',
            'eye and lip care': 'skincare', 'creams': 'skincare', 'cleansers': 'skincare', 'lotions': 'skincare', 'uv protection': 'skincare'
        }
    };

    var dataLayer = (window.dataLayer = window.dataLayer || []);
    var nativeFetch = window.fetch;
    var root = (G.page.root || '/').replace(/\/?$/, '/');

    function lc(value) {
        return String(value == null ? '' : value).toLowerCase().trim();
    }

    // Shopify's page_title arrives HTML-escaped.
    function unescapeHtml(value) {
        return String(value || '').replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
    }

    function money(cents) {
        return Math.round(cents) / 100;
    }

    /* ---------- products ---------- */

    function isGift(product) {
        return (product.tags || []).some(function (tag) {
            return CONFIG.giftTags.indexOf(lc(tag)) > -1;
        });
    }

    // { axis, category, subCategory } e.g. makeup / face / foundation
    function taxonomy(product) {
        var type = lc(product.type);
        var tags = (product.tags || []).map(lc);
        var category = tags.filter(function (tag) { return CONFIG.tagAxes[tag]; })[0] || '';
        return {
            axis: CONFIG.axes[type] || CONFIG.tagAxes[category] || '',
            category: category,
            subCategory: type
        };
    }

    function optionValue(product, variant, pattern) {
        var names = (product.options || []).map(function (option) {
            return lc(option && option.name ? option.name : option);
        });
        for (var i = 0; i < names.length; i++) {
            if (pattern.test(names[i]) && variant.options && variant.options[i]) return lc(variant.options[i]);
        }
        return 'none';
    }

    function findVariant(product, variantId) {
        return (product.variants || []).filter(function (variant) {
            return String(variant.id) === String(variantId);
        })[0];
    }

    // Raw Shopify product + variant -> the product object of the tracking plan.
    function normalise(product, variant, overrides) {
        var tax = taxonomy(product);
        var price = variant.price;
        var original = variant.compare_at_price > price ? variant.compare_at_price : price;
        var rating = parseFloat(product.rating);
        var item = {
            name: lc(product.title),
            // Short tagline under the product title (metafield custom.product_sub_title).
            subname: product.subtitle ? lc(product.subtitle) : 'none',
            id: String(product.id),
            price: money(price),
            brand: CONFIG.brand,
            category: [tax.axis, tax.category, tax.subCategory].filter(Boolean).join('/') || 'none',
            variant: /^default title$/i.test(variant.title) ? 'none' : lc(variant.title),
            dimension35: optionValue(product, variant, /size|volume/),
            dimension36: optionValue(product, variant, /colou?r|shade/),
            dimension37: optionValue(product, variant, /fragrance|scent/),
            dimension38: isNaN(rating) ? 'none' : Math.round(rating * 10) / 10,
            dimension39: variant.available ? 'in stock' : 'out of stock',
            dimension40: 'not present',
            dimension48: variant.barcode ? String(variant.barcode) : 'none',
            dimension49: 'not present',
            dimension50: money(original),
            dimension59: Number(product.rating_count) || 0,
            dimension91: 'none',
            dimension151: product.badge ? String(product.badge) : 'none',
            dimension153: 'not present',
            dimension154: 'none',
            dimension167: 'none',
            dimension168: 'notActivated',
            dimension169: 'none',
            metric21: money(original),
            metric52: variant.available ? 0 : 1
        };
        for (var key in overrides) item[key] = overrides[key];
        return item;
    }

    var productCache = {};

    function cacheProduct(product) {
        var cached = productCache[product.handle];
        if (!cached) {
            productCache[product.handle] = product;
            return;
        }
        product.variants.forEach(function (variant) {
            if (!findVariant(cached, variant.id)) cached.variants.push(variant);
        });
    }

    // Resolves with the raw product, fetching /products/<handle>.js when the
    // page did not already render that variant.
    function getProduct(handle, variantId) {
        var cached = productCache[handle];
        if (cached && findVariant(cached, variantId)) return Promise.resolve(cached);
        return nativeFetch(root + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
            .then(function (response) { return response.json(); })
            .then(function (product) {
                if (cached) {
                    product.rating = cached.rating;
                    product.rating_count = cached.rating_count;
                    product.badge = cached.badge;
                    product.subtitle = cached.subtitle;
                    productCache[handle] = product;
                    return product;
                }
                // The .js endpoint has no metafields, so read the subtitle from the product page.
                return nativeFetch(root + 'products/' + encodeURIComponent(handle), { credentials: 'same-origin' })
                    .then(function (response) { return response.text(); })
                    .then(function (html) {
                        var match = /class="product-sub-title"[^>]*>([\s\S]*?)<\/div>/.exec(html);
                        product.subtitle = match ? match[1].replace(/<[^>]*>/g, '').trim() : '';
                    })
                    .catch(function () {})
                    .then(function () {
                        productCache[handle] = product;
                        return product;
                    });
            });
    }

    /* ---------- cart snapshot ---------- */

    // begin_checkout has to be pushed synchronously on click, so the cart is kept
    // ready in memory and refreshed after every cart request.
    var cartItems = G.cart.items || [];
    // Quantity per variant as last reported by Shopify, to tell how much a cart
    // change added. Updated synchronously from every cart response.
    var cartQuantities = {};
    cartItems.forEach(function (item) {
        cartQuantities[item.variant_id] = (cartQuantities[item.variant_id] || 0) + item.quantity;
    });
    cartItems.forEach(function (item) { cacheProduct(item.product); });
    if (G.product) cacheProduct(G.product);

    function cartProducts() {
        // Free samples / gifts are listed too (price 0), as on the KIE MY reference.
        return cartItems.filter(function (item) {
            return findVariant(item.product, item.variant_id);
        }).map(function (item) {
            return normalise(item.product, findVariant(item.product, item.variant_id), {
                price: money(item.final_price),
                quantity: item.quantity
            });
        });
    }

    function persistProducts() {
        try {
            var stored = {};
            if (G.product) {
                G.product.variants.forEach(function (variant) {
                    stored[variant.id] = normalise(G.product, variant);
                });
            }
            cartItems.forEach(function (item) {
                var variant = findVariant(item.product, item.variant_id);
                if (!variant) return;
                stored[item.variant_id] = normalise(item.product, variant);
                stored[item.variant_id].gift = isGift(item.product);
            });
            window.localStorage.setItem(CONFIG.storageKey, JSON.stringify(stored));
        } catch (error) {}
    }

    var refreshTimer;

    function refreshCart() {
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(function () {
            nativeFetch(root + 'cart.js', { credentials: 'same-origin' })
                .then(function (response) { return response.json(); })
                .then(function (cart) {
                    return Promise.all(cart.items.map(function (line) {
                        return getProduct(line.handle, line.variant_id).then(function (product) {
                            return {
                                variant_id: line.variant_id,
                                quantity: line.quantity,
                                final_price: line.final_price,
                                product: product
                            };
                        });
                    }));
                })
                .then(function (items) {
                    cartItems = items;
                    cartQuantities = {};
                    items.forEach(function (item) {
                        cartQuantities[item.variant_id] = (cartQuantities[item.variant_id] || 0) + item.quantity;
                    });
                    persistProducts();
                })
                .catch(function () {});
        }, 300);
    }

    /* ---------- pageDataReady ---------- */

    function pageCategory() {
        var page = G.page;
        var handle = lc(page.handle);
        switch (page.type) {
            case 'index': return 'homepage';
            case 'product': return 'product detail page';
            case 'collection':
            case 'list-collections': return 'product results page';
            case 'search': return 'internal search';
            case 'cart': return 'checkout page';
            case '404': return 'error page';
            case 'article':
            case 'blog': return 'content page::article';
            case 'page':
                if (/store-locator/.test(handle)) return 'store locator';
                if (/service/.test(handle)) return 'content page::service';
                if (/faq|shipping|return|payment|privacy|terms|contact|support|policy/.test(handle)) return 'appendix';
                return 'content page::branding page';
        }
        if (/^customers/.test(lc(page.type)) || /^customers/.test(lc(page.template))) return 'my account';
        return 'appendix';
    }

    function breadCrumb(tax) {
        var page = G.page;
        var trail = ['home'];
        if (page.type === 'product' && G.product) {
            trail.push(tax.axis, tax.category, tax.subCategory, lc(G.product.title));
        } else if (page.type === 'collection' && page.collection) {
            trail.push(lc(page.collection.title));
        } else if (page.type === 'article' && page.article) {
            trail.push(lc(page.article.blog), lc(page.article.title));
        } else if (page.type === 'cart') {
            trail.push('cart');
        } else if (page.type === 'search') {
            trail.push('search');
        } else if (page.type !== 'index') {
            trail.push(lc(unescapeHtml(page.title)));
        }
        return trail.filter(Boolean).join('>');
    }

    // SHA-256 of the trimmed, lowercased email, as hex. Empty when unavailable.
    function sha256(text) {
        try {
            if (!window.crypto || !window.crypto.subtle || !window.TextEncoder) return Promise.resolve('');
            return window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)).then(function (buffer) {
                return Array.prototype.map.call(new Uint8Array(buffer), function (byte) {
                    return ('0' + byte.toString(16)).slice(-2);
                }).join('');
            }).catch(function () { return ''; });
        } catch (error) {
            return Promise.resolve('');
        }
    }

    // uiEmailId: only for logged-in visitors who accepted targeting cookies (OneTrust group 4).
    function emailId() {
        var email = G.customer && G.customer.email;
        var targeting = (window.OnetrustActiveGroups || '').indexOf(',4,') > -1;
        return email && targeting ? sha256(lc(email)) : Promise.resolve('');
    }

    function pageData(hashedEmail) {
        var page = G.page;
        var customer = G.customer;
        var tax = G.product ? taxonomy(G.product) : { axis: '', category: '', subCategory: '' };
        var data = {
            event: 'pageDataReady',
            brand: CONFIG.brand,
            language: lc(page.language),
            country: String(page.country || '').toUpperCase(),
            // Unpublished/preview themes report as "testing" so QA traffic can be filtered out.
            siteTypeLevel: page.theme_role === 'main' ? 'main' : 'testing',
            pageCategory: pageCategory(),
            website_type: CONFIG.websiteType,
            siteSection: CONFIG.siteSection,
            uiLoggedStatus: customer ? 'regular logged' : 'not logged',
            breadCrumb: breadCrumb(tax),
            time_stamp: String(Date.now())
        };

        if (G.product) {
            data.section = tax.axis;
            data.category = tax.category;
            data.subCategory = tax.subCategory;
            data.axes = tax.axis;
        } else if (page.collection) {
            // Collections have no product type, so the axis comes from the collection itself.
            var collectionAxis = /make-?up/.test(lc(page.handle) + lc(page.collection.title)) ? 'makeup'
                : /fragrance|parfum|perfume/.test(lc(page.handle) + lc(page.collection.title)) ? 'fragrance'
                : /skin/.test(lc(page.handle) + lc(page.collection.title)) ? 'skincare' : 'all axes';
            data.section = collectionAxis;
            data.category = lc(page.collection.title);
            data.subCategory = 'all';
            data.axes = collectionAxis;
        }

        if (customer) {
            data.uiUser = String(customer.id);
            data.uiCity = lc(customer.city);
            data.uiPostalCode = customer.zip;
            data.newsletterSubscription = String(!!customer.accepts_marketing);
            if (customer.accepts_marketing) data.subscriptionType = 'email';
        }

        if (hashedEmail) data.uiEmailId = hashedEmail;

        if (customer && customer.orders_count > 0) data.ecommerceStatus = 'return buyer';
        else if (cartItems.length) data.ecommerceStatus = 'cart abandoner';
        else if (page.type === 'product') data.ecommerceStatus = 'viewer';

        if (cartItems.length) data.uiCartAmount = money(G.cart.total_price);

        if (page.search) {
            data.searchCategory = 'Internal';
            data.searchKeyword = page.search.terms;
            data.numberResults = page.search.results;
        }

        if (page.article) {
            var tags = (page.article.tags || []).map(lc);
            data.articleId = String(page.article.id);
            data.articleName = page.article.title;
            data.articleCategory = lc(page.article.blog);
            data.articleAuthor = lc(page.article.author);
            data.publicationMonth = Number(page.article.month);
            data.publicationYear = Number(page.article.year);
            data.mainArticleTag = tags[0];
            data.allArticleTag = tags.join('::');
        }

        // A parameter with no value must not exist in the dataLayer at all.
        for (var name in data) {
            if (data[name] === undefined || data[name] === null || data[name] === '') delete data[name];
        }
        return data;
    }

    /* ---------- start: pageDataReady, GTM, view_item ---------- */

    // Order in the dataLayer: OneTrust events, pageDataReady, gtm.js, view_item.
    // OneTrust loads its full script late, so this waits for OptanonWrapper
    // (G.otDone) and gives up after 3 seconds so tags are never blocked.
    function pushViewItem() {
        if (G.page.type === 'product' && G.product) {
            var viewed = findVariant(G.product, G.selected_variant_id) || G.product.variants[0];
            if (viewed) {
                var viewedItem = normalise(G.product, viewed);
                var viewedLabel = viewedItem.name + '::' + viewedItem.id;
                dataLayer.push({
                    event: 'nievent',
                    eventCategory: 'Ecommerce',
                    eventAction: 'Product Detail',
                    eventLabel: viewedLabel,
                    numberReviews: viewedItem.dimension59,
                    event_name: 'view_item',
                    event_detail: 'product',
                    product_info: viewedLabel,
                    ecommerce: {
                        currencyCode: G.page.currency,
                        detail: { actionField: {}, products: [viewedItem] }
                    }
                });
            }
        }
    }

    var started = false;

    // OptanonWrapper can run before OneTrust has pushed its own events, which happens
    // for visitors who have not chosen yet. pageDataReady and GTM wait for OptanonLoaded too.
    function oneTrustPushed() {
        return (window.dataLayer || []).some(function (entry) {
            return entry && entry.event === 'OptanonLoaded';
        });
    }

    G.start = function () {
        if (started || !G.loadGtm || !(G.timedOut || (G.otDone && oneTrustPushed()))) return;
        started = true;
        emailId().then(function (hashedEmail) {
            dataLayer.push(pageData(hashedEmail));
            G.loadGtm();
            pushViewItem();
        });
    };

    // OptanonLoaded has no callback of its own, so check for it until it shows up.
    var waitForOneTrust = setInterval(function () {
        if (started) clearInterval(waitForOneTrust);
        else G.start();
    }, 50);

    setTimeout(function () {
        G.timedOut = true;
        G.start();
    }, 3000);

    /* ---------- add_to_cart ---------- */

    function scrollPercentage() {
        var doc = document.documentElement;
        var height = Math.max(doc.scrollHeight, document.body ? document.body.scrollHeight : 0);
        if (!height) return 0;
        return Math.min(100, Math.round(((window.pageYOffset || doc.scrollTop) + window.innerHeight) / height * 100));
    }

    // Quantities asked for in a /cart/add request body, keyed by variant id.
    function requestedQuantities(body) {
        var quantities = {};
        function add(id, quantity) {
            if (id) quantities[String(id)] = parseInt(quantity, 10) || 1;
        }
        function fromPairs(get, keys) {
            add(get('id'), get('quantity'));
            keys.forEach(function (key) {
                var match = /^items\[(\d+)\]\[id\]$/.exec(key);
                if (match) add(get(key), get('items[' + match[1] + '][quantity]'));
            });
        }
        try {
            if (typeof FormData !== 'undefined' && body instanceof FormData) {
                var formKeys = [];
                body.forEach(function (value, key) { formKeys.push(key); });
                fromPairs(function (key) { return body.get(key); }, formKeys);
            } else if (typeof body === 'string' && /^\s*\{/.test(body)) {
                var json = JSON.parse(body);
                (json.items || [json]).forEach(function (item) { add(item.id, item.quantity); });
            } else if (typeof body === 'string' || body instanceof URLSearchParams) {
                var params = new URLSearchParams(body);
                var paramKeys = [];
                params.forEach(function (value, key) { paramKeys.push(key); });
                fromPairs(function (key) { return params.get(key); }, paramKeys);
            }
        } catch (error) {}
        return quantities;
    }

    function pushAddToCart(line, quantity) {
        getProduct(line.handle, line.variant_id).then(function (product) {
            var variant = findVariant(product, line.variant_id);
            if (!variant || isGift(product)) return;
            var item = normalise(product, variant, {
                price: money(line.final_price != null ? line.final_price : variant.price),
                quantity: quantity
            });
            dataLayer.push({
                event: 'addToCart',
                eventCategory: 'Ecommerce',
                eventAction: 'Add to Cart',
                eventLabel: item.name + '::' + item.id,
                scroll_percentage: scrollPercentage(),
                event_name: 'add_to_cart',
                ecommerce: {
                    currencyCode: G.page.currency,
                    add: { products: [item] }
                }
            });
        }).catch(function () {});
    }

    function onCartAdd(response, body) {
        var lines = response && response.items ? response.items : [response];
        var quantities = requestedQuantities(body);
        lines.forEach(function (line) {
            if (!line || !line.handle || !line.variant_id) return;
            if (line.quantity) cartQuantities[line.variant_id] = line.quantity;
            pushAddToCart(line, quantities[String(line.variant_id)] || 1);
        });
        refreshCart();
    }

    // /cart/change and /cart/update answer with the whole cart. A variant whose
    // quantity went up (the "+" in the cart page and side cart) counts as an add.
    function onCartChange(cart) {
        if (!cart || !cart.items) return refreshCart();
        var next = {};
        var lines = {};
        cart.items.forEach(function (line) {
            next[line.variant_id] = (next[line.variant_id] || 0) + line.quantity;
            lines[line.variant_id] = lines[line.variant_id] || line;
        });
        for (var id in next) {
            var added = next[id] - (cartQuantities[id] || 0);
            if (added > 0 && lines[id].handle) pushAddToCart(lines[id], added);
        }
        cartQuantities = next;
        refreshCart();
    }

    // The theme adds to cart from many places (fetch and jQuery.ajax), so the
    // cart endpoints are observed instead of each button.
    function cartRequest(url) {
        var match = /\/cart\/(add|change|update|clear)(?:\.js|\.json)?(?:[?#]|$)/.exec(String(url || ''));
        return match ? match[1] : null;
    }

    if (nativeFetch) {
        window.fetch = function (input, init) {
            var promise = nativeFetch.apply(this, arguments);
            try {
                var kind = cartRequest(typeof input === 'string' ? input : input && input.url ? input.url : input);
                if (kind) {
                    var body = init && init.body;
                    promise.then(function (response) {
                        if (!response || !response.ok) return;
                        if (kind === 'clear') { cartQuantities = {}; return refreshCart(); }
                        return response.clone().json().then(function (json) {
                            if (kind === 'add') onCartAdd(json, body);
                            else onCartChange(json);
                        });
                    }).catch(function () {});
                }
            } catch (error) {}
            return promise;
        };
    }

    var xhrOpen = XMLHttpRequest.prototype.open;
    var xhrSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
        this._gabCartRequest = cartRequest(url);
        return xhrOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function (body) {
        var xhr = this;
        var kind = xhr._gabCartRequest;
        if (kind) {
            xhr.addEventListener('load', function () {
                if (xhr.status < 200 || xhr.status >= 300) return;
                if (kind === 'clear') { cartQuantities = {}; return refreshCart(); }
                try {
                    var json = JSON.parse(xhr.responseText);
                    if (kind === 'add') onCartAdd(json, body);
                    else onCartChange(json);
                } catch (error) {}
            });
        }
        return xhrSend.apply(this, arguments);
    };

    /* ---------- begin_checkout ---------- */

    var lastCheckoutPush = 0;

    document.addEventListener('click', function (event) {
        var button = event.target && event.target.closest ? event.target.closest(CONFIG.checkoutSelector) : null;
        if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        // The theme forwards some clicks to the real button; count them once.
        var now = Date.now();
        if (now - lastCheckoutPush < 2000) return;

        var products = cartProducts();
        if (!products.length) return;
        lastCheckoutPush = now;

        dataLayer.push({
            event: 'checkout',
            eventCategory: 'ecommerce',
            eventAction: 'select::checkout',
            eventLabel: 'regular',
            event_name: 'begin_checkout',
            ecommerce: { products: products }
        });
    }, true);

    persistProducts();
})();
